/**
 * MobileEndingContractsTab
 *
 * Card-based mobile list replacing the PrimeReact DataTable from EndingContractsTab.
 * Glassmorphic cards, search, pagination.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { Localize } from "../../../app/lang/langs";
import { getEndingContracts } from "../../../components/clients/clientsSlice";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 15;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const formatDate = (date) => {
  if (!date) return "-";
  return moment(date).format("DD/MM/YYYY");
};

const getEndDateColor = (endDate) => {
  if (!endDate) return "#666";
  const daysLeft = moment(endDate).diff(moment(), "days");
  if (daysLeft < 0) return "#d32f2f";
  if (daysLeft <= 30) return "#ed6c02";
  if (daysLeft <= 90) return "#1976d2";
  return "#2e7d32";
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileEndingContractsTab() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mounted = useRef(null);

  const endingContracts = useSelector(
    (state) => state.clients.endingContracts
  );
  const selectedTeamsIds = useSelector(
    (state) => state.teams.selectedTeamsIds
  );

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const canAccessClientsTeams = checkPermission(config.scopes.clientsTeams);

  // Fetch data on mount
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getEndingContracts());
    }
  });

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchText]);

  // Filter and search
  const filteredList = useMemo(() => {
    let data = endingContracts || [];

    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      data = data.filter((c) => {
        const searchStr = [
          c.poste,
          c.collab_last_name,
          c.collab_name,
          c.team_name,
          c.client_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    return data;
  }, [endingContracts, searchText]);

  const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
  const paginatedList = filteredList.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Handlers ---- */
  const handleTeamTap = (contract) => {
    if (contract.team_name) {
      const encodedLabel = encodeURIComponent(contract.team_name);
      navigate(`/clients/${selectedTeamsIds[0]}/1/${encodedLabel}`);
    }
  };

  /* ---- Render ---- */
  return (
    <>
      {/* ---- Filters header ---- */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0,
          flexShrink: 0,
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
        }}
      >
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: "rgba(0,0,0,0.35)" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& input": { fontSize: "16px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              backgroundColor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(12px)",
              transition: TRANSITION_SMOOTH,
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.06)",
                transition: TRANSITION_SMOOTH,
              },
              "&:hover fieldset": {
                borderColor: "rgba(0,0,0,0.15)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,144,21,0.5)",
                boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
              },
            },
          }}
        />

        {/* Results count */}
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            px: 0.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "rgba(0,0,0,0.45)",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            display: "block",
          }}
        >
          {filteredList.length} {Localize("results")}
        </Typography>
      </Box>

      {/* ---- Card list ---- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: 2,
          pt: 1.5,
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,144,21,0.18)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedList.map((contract, index) => {
          const borderColor = getEndDateColor(contract.team_end_date);
          return (
            <Fade in key={contract.id || index} timeout={300 + index * 50}>
              <Card
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: "18px",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 100%)",
                  backdropFilter: "blur(20px) saturate(1.6)",
                  WebkitBackdropFilter: "blur(20px) saturate(1.6)",
                  border: "1px solid rgba(255,255,255,0.6)",
                  borderLeft: `4px solid ${borderColor}`,
                  boxShadow: `0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9), 0 0 0 0.5px ${borderColor}15`,
                  transition: TRANSITION_SMOOTH,
                  "&:hover": {
                    boxShadow: `0 12px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04), 0 0 20px ${borderColor}12`,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "scale(0.97)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  },
                }}
              >
                <CardActionArea sx={{ p: 0, minHeight: 44 }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    {/* Row 1: Icon + Poste + Client chip */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {/* Avatar icon */}
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: "14px",
                            background:
                              "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow:
                              "0 4px 14px rgba(255,144,21,0.3), 0 1px 4px rgba(255,144,21,0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
                            transition: TRANSITION_SMOOTH,
                          }}
                        >
                          <WorkOutlineIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "1rem",
                              lineHeight: 1.3,
                              letterSpacing: "-0.02em",
                              color: "rgba(0,0,0,0.9)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {contract.poste || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "rgba(0,0,0,0.55)",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {contract.collab_last_name}{" "}
                            {contract.collab_name}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Client chip */}
                      {contract.client_name ? (
                        <Chip
                          label={contract.client_name}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.67rem",
                            height: 28,
                            borderRadius: "999px",
                            flexShrink: 0,
                            maxWidth: 150,
                            bgcolor: "rgba(46,125,50,0.1)",
                            color: "#2e7d32",
                            border: "1px solid rgba(46,125,50,0.25)",
                            boxShadow: "0 2px 6px rgba(46,125,50,0.12)",
                            transition: TRANSITION_SMOOTH,
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      ) : (
                        <Chip
                          label="N/A"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.67rem",
                            height: 28,
                            borderRadius: "999px",
                            flexShrink: 0,
                            bgcolor: "rgba(237,108,2,0.1)",
                            color: "#ed6c02",
                            border: "1px solid rgba(237,108,2,0.25)",
                            boxShadow: "0 2px 6px rgba(237,108,2,0.12)",
                            transition: TRANSITION_SMOOTH,
                          }}
                        />
                      )}
                    </Box>

                    {/* Row 2: Details */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 7,
                      }}
                    >
                      {/* Team (tappable only when user has clientsTeams access) */}
                      {contract.team_name && (
                        <Box
                          onClick={canAccessClientsTeams ? (e) => {
                            e.stopPropagation();
                            handleTeamTap(contract);
                          } : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            cursor: canAccessClientsTeams ? "pointer" : "default",
                            transition: TRANSITION_SMOOTH,
                            borderRadius: "8px",
                            mx: -0.5,
                            px: 0.5,
                            py: 0.25,
                            ...(canAccessClientsTeams && {
                              "&:hover": {
                                backgroundColor: "rgba(25,118,210,0.06)",
                              },
                              "&:active": {
                                opacity: 0.7,
                                transform: "scale(0.98)",
                              },
                            }),
                          }}
                        >
                          <GroupsOutlinedIcon
                            sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.8rem",
                              color: canAccessClientsTeams ? "#1976d2" : "rgba(0,0,0,0.6)",
                              fontWeight: 600,
                              textDecoration: canAccessClientsTeams ? "underline" : "none",
                              textDecorationColor: "rgba(25,118,210,0.3)",
                              transition: TRANSITION_SMOOTH,
                            }}
                          >
                            {contract.team_name}
                          </Typography>
                        </Box>
                      )}

                      {/* Start date */}
                      {contract.team_start_date && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <EventOutlinedIcon
                            sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.8rem",
                              color: "rgba(0,0,0,0.6)",
                              fontWeight: 500,
                            }}
                          >
                            {Localize("START_DATE")}:{" "}
                            {formatDate(contract.team_start_date)}
                          </Typography>
                        </Box>
                      )}

                      {/* End date */}
                      {contract.team_end_date && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <EventOutlinedIcon
                            sx={{
                              fontSize: "0.9rem",
                              color: getEndDateColor(contract.team_end_date),
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.8rem",
                              color: getEndDateColor(contract.team_end_date),
                              fontWeight: 600,
                            }}
                          >
                            {Localize("END_DATE")}:{" "}
                            {formatDate(contract.team_end_date)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Fade>
          );
        })}

        {/* Empty state */}
        {paginatedList.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "24px",
                  background:
                    "linear-gradient(135deg, rgba(255,144,21,0.15) 0%, rgba(255,193,0,0.1) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,144,21,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  boxShadow:
                    "0 8px 32px rgba(255,144,21,0.12), 0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <InboxOutlinedIcon
                  sx={{ fontSize: 36, color: "#FF9015" }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "rgba(0,0,0,0.4)", fontWeight: 500 }}
              >
                Aucun contrat en fin trouvé.
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* ---- Pagination ---- */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 1.5,
            pb: 2,
            backgroundColor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(16px) saturate(1.4)",
            WebkitBackdropFilter: "blur(16px) saturate(1.4)",
            borderTop: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                  background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                },
              },
            }}
          />
        </Box>
      )}
    </>
  );
}
